var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var Socket_1;
import { inject, injectable } from 'inversify';
import { TokenService } from '../http';
import { Logger } from '../logger';
const SUCCESS_CLOSE_CODE = 1000;
const ATTEMPTS = 5;
const RECONNECT_BACKOFF_BASE = 2;
const RECONNECT_DELAY = 5000;
const RECONNECT_JITTER = 1000;
const HEARTBEAT_INTERVAL = 30000;
const PONG_TIMEOUT = 10000;
const PING_MESSAGE = JSON.stringify({ type: 'ping' });
let Socket = Socket_1 = class Socket {
    constructor(token, logger) {
        this.logger = logger;
        this.listeners = new Map();
        this.activeSubscriptions = [];
        this.pendingClientMessages = [];
        this.token = token;
        this.reconnectAttempt = 0;
        this.heartbeatTimer = null;
        this.pongTimer = null;
        this.connectionStatusListeners = new Set();
    }
    /** Поддерживает ли окружение WebSocket. */
    static get isSupported() {
        return typeof WebSocket !== 'undefined';
    }
    /** Текущее состояние подключения. */
    isConnected() {
        var _a;
        return ((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN;
    }
    /** Подписаться на изменения статуса подключения.
     * Колбэк вызывается с актуальным состоянием при каждом open/close.
     * Возвращает функцию отписки.
     */
    onConnectionStatus(callback) {
        this.connectionStatusListeners.add(callback);
        return () => {
            this.connectionStatusListeners.delete(callback);
        };
    }
    notifyConnectionStatus(isConnected) {
        this.connectionStatusListeners.forEach((listener) => listener(isConnected));
    }
    /** Выполнить подключение к Websocket серверу
     * Осуществляется подписка на события:
     * onopen - срабатывает, когда соединение с Websocket установлено и становится досупен двусторонний обмен сообщениями
     * onmessage - срабатывает каждый раз, когда от Websocket получено сообщение, и происходит рассылка всем подписчикам
     * onerror - срабатывает, когда на Websocket произошла ошибка, логирует сообщение об ошибке будет залогировано
     * onclose - срабатывает, когда Websocket соединение переходит в статус "closed", может выполнить переподключение
     */
    connect() {
        if (!Socket_1.isSupported) {
            this.notifyConnectionStatus(false);
            return;
        }
        if (this.ws) {
            this.stopHeartbeat();
            this.detachWsHandlers(this.ws);
            this.ws.close();
            this.ws = null;
        }
        this.ws = new WebSocket(`${window.REACT_APP_VKHRTEK_WS_API_URL}/ws/connect`);
        this.ws.onopen = () => {
            const token = this.token.get();
            if (this.ws && token) {
                this.ws.send(token);
            }
            if (this.activeSubscriptions.length) {
                this.sendClientMessage({
                    type: 'subscribe',
                    payload: { subscriptions: this.activeSubscriptions },
                });
            }
            this.flushPendingClientMessages();
            this.reconnectAttempt = 0;
            this.startHeartbeat();
            this.notifyConnectionStatus(true);
        };
        this.ws.onmessage = (message) => {
            const { type, payload } = JSON.parse(message.data);
            // Любое входящее сообщение означает, что соединение живо
            this.clearPongTimer();
            if (type === 'pong') {
                return;
            }
            const typeListeners = this.listeners.get(type);
            if (typeListeners) {
                typeListeners.forEach((listener) => listener(payload));
            }
        };
        this.ws.onerror = (error) => {
            // Событие ошибки WS приходит синхронно внутри стека вызовов React
            // (рендера компонентов). Откладываем логирование в отдельную задачу,
            // чтобы Sentry не прикреплял посторонний call stack React и ошибка WS
            // не влияла на рендер.
            setTimeout(() => {
                this.logger.warning(error, {
                    tags: {
                        vkdoc_error_type: 'ws',
                        http_method: 'WS',
                        http_url: `${window.REACT_APP_VKHRTEK_WS_API_URL}/ws/connect`,
                    },
                });
            });
        };
        this.ws.onclose = (message) => {
            this.stopHeartbeat();
            this.notifyConnectionStatus(false);
            if (message.code !== SUCCESS_CLOSE_CODE &&
                this.reconnectAttempt < ATTEMPTS) {
                // 5с, 10с, 20с, ... + jitter, чтобы тысячи клиентов не переподключались одновременно
                const backoff = Math.pow(RECONNECT_BACKOFF_BASE, this.reconnectAttempt) * RECONNECT_DELAY;
                const jitter = Math.random() * RECONNECT_JITTER;
                this.reconnectAttempt++;
                setTimeout(() => {
                    this.connect();
                }, backoff + jitter);
            }
            else {
                this.resetWs();
            }
        };
    }
    /** Добавить подписчика на сообщения определённого типа от Websocket.
     * Возвращает функцию отписки конкретного listener'а.
     * @param type - Тип сообщения от Websocket
     * @param callback - Подписчик на сообщения от Websocket
     */
    on(type, callback) {
        var _a;
        const typeListeners = (_a = this.listeners.get(type)) !== null && _a !== void 0 ? _a : new Set();
        typeListeners.add(callback);
        this.listeners.set(type, typeListeners);
        return () => this.off(type, callback);
    }
    /** Удалить ранее добавленного подписчика. */
    off(type, callback) {
        const typeListeners = this.listeners.get(type);
        if (!typeListeners) {
            return;
        }
        typeListeners.delete(callback);
    }
    /** Подписаться на изменения по топику.
     * Если топик объектный, нужно передать objectId.
     * Сообщение отправится сразу при открытом соединении, иначе будет
     * отправлено в onopen.
     */
    subscribe(topic, objectId) {
        const subscription = Object.assign({ topic }, (objectId ? { object_id: objectId } : {}));
        if (this.hasSubscription(subscription)) {
            return;
        }
        this.activeSubscriptions.push(subscription);
        this.sendClientMessage({
            type: 'subscribe',
            payload: { subscriptions: [subscription] },
        });
    }
    /** Отписаться от топика, симметрично subscribe. */
    unsubscribe(topic, objectId) {
        const target = Object.assign({ topic }, (objectId ? { object_id: objectId } : {}));
        const index = this.activeSubscriptions.findIndex((subscription) => this.isSameSubscription(subscription, target));
        if (index === -1) {
            return;
        }
        this.activeSubscriptions.splice(index, 1);
        this.sendClientMessage({
            type: 'unsubscribe',
            payload: { subscriptions: [target] },
        });
    }
    /** Закрыть Websocket соединение */
    resetWs() {
        this.stopHeartbeat();
        if (this.ws) {
            this.detachWsHandlers(this.ws);
            this.ws.close();
            this.ws = null;
        }
        this.unsubscribeAll();
        this.activeSubscriptions = [];
        this.pendingClientMessages = [];
        this.notifyConnectionStatus(false);
    }
    /** Отвязать обработчики от старого WebSocket-экземпляра */
    detachWsHandlers(ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
    }
    /**
     * Запустить heartbeat: периодически отправлять ping и ждать ответ.
     * Если за PONG_TIMEOUT не пришло ни одного сообщения — соединение считается
     * "мёртвым" (полуоткрытым) и закрывается, что инициирует переподключение
     * через onclose.
     */
    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            var _a;
            if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) !== WebSocket.OPEN) {
                return;
            }
            this.ws.send(PING_MESSAGE);
            this.pongTimer = setTimeout(() => {
                var _a;
                (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
            }, PONG_TIMEOUT);
        }, HEARTBEAT_INTERVAL);
    }
    /** Остановить heartbeat и сбросить таймеры ping/pong */
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        this.clearPongTimer();
    }
    /** Сбросить таймер ожидания pong — соединение подтвердило свою активность */
    clearPongTimer() {
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;
        }
    }
    hasSubscription(target) {
        return this.activeSubscriptions.some((subscription) => this.isSameSubscription(subscription, target));
    }
    isSameSubscription(a, b) {
        return a.topic === b.topic && a.object_id === b.object_id;
    }
    sendClientMessage(message) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            return;
        }
        this.pendingClientMessages.push(message);
    }
    flushPendingClientMessages() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            return;
        }
        const pending = this.pendingClientMessages;
        this.pendingClientMessages = [];
        pending.forEach((message) => {
            var _a;
            (_a = this.ws) === null || _a === void 0 ? void 0 : _a.send(JSON.stringify(message));
        });
    }
    unsubscribeAll() {
        this.listeners.clear();
    }
};
Socket = Socket_1 = __decorate([
    injectable(),
    __param(0, inject(TokenService)),
    __metadata("design:paramtypes", [Object, Logger])
], Socket);
export { Socket };
